import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';

import { errorNotification } from '../../components/notifications';
import api from '../../libs/api';

const initialState = {
  transactions: {
    list: [],
    page: 1,
    query: { page: 1 },
    totalPages: 1,
    totalTransactions: 0,
  },
  transaction: {},
  transactionIds: [],
  files: [],
  bankTransactions: {
    transactions: [],
    unprocessedTransactions: [],
    bankInfo: {},
  },
};

export const getTransactions = createAsyncThunk(
  'transactions/getTransactions',
  async (query, { getState }) => {
    const currentQuery = getState().transactions.transactions.query;

    const effectiveQuery = query || currentQuery;

    const { data } = await api.get('/accounts/transactions', effectiveQuery);

    return { data, query: effectiveQuery };
  },
);

export const getBankTransactions = createAsyncThunk(
  'transactions/getBankTransactions',
  async query => {
    const { data } = await api.get(
      '/accounts/transactions/banktransactions',
      query,
    );

    return data;
  },
);

export const getTransactionIds = createAsyncThunk(
  'transactions/getTransactionIds',
  async () => {
    const { data } = await api.get('/accounts/transactions/ids');

    return data;
  },
);

export const getTransaction = createAsyncThunk(
  'transactions/getTransaction',
  async id => {
    const { data } = await api.get(`/accounts/transactions/${id}`);

    return data;
  },
);

export const getTransactionFiles = createAsyncThunk(
  'transactions/getTransactionFiles',
  async transactionId => {
    const { data } = await api.get(
      `/accounts/transactions/files/${transactionId}`,
    );

    return data;
  },
);

export const deleteTransactionFile = createAsyncThunk(
  'transactions/deleteTransactionFiles',
  async ({ fileId, transactionId }) => {
    await api.delete(`/accounts/transactions/files/${fileId}`);

    return { fileId, transactionId };
  },
);

export const addTransactionFiles = createAsyncThunk(
  'transactions/addTransactionFiles',
  async ({ transactionId, files }) => {
    if (!files || !files.length) {
      throw new Error('Please select files first');
    }

    const formData = new FormData();

    files.forEach((file, index) => {
      formData.append(`files[${index}]`, file);
    });

    const { data } = await api.post(
      `/accounts/transactions/files/${transactionId}`,
      formData,
      {
        headers: {
          'content-type': 'multipart/form-data',
        },
      },
    );

    return { data, transactionId };
  },
);

const transactions = createSlice({
  name: 'transactions',

  initialState,

  reducers: {
    clearTransactions: draft => {
      draft.transactions = initialState.transactions;
    },

    clearTransaction: draft => {
      draft.transaction = initialState.transaction;
    },

    clearTransactionIds: draft => {
      draft.transactionIds = initialState.transactionIds;
    },

    clearTransactionFiles: draft => {
      draft.files = initialState.files;
    },

    clearBankTransactions: draft => {
      draft.bankTransactions = initialState.bankTransactions;
    },
  },

  extraReducers: builder => {
    builder.addCase(getBankTransactions.fulfilled, (draft, { payload }) => {
      draft.bankTransactions = payload;
    });

    builder.addCase(getTransactions.fulfilled, (draft, { payload }) => {
      draft.transactions.list = payload.data.transactions;
      draft.transactions.page = payload.data.page;
      draft.transactions.query = payload.query;
      draft.transactions.totalTransactions = payload.data.totalTransactions;
      draft.transactions.totalPages = payload.data.totalPages;
    });

    builder.addCase(getTransaction.fulfilled, (draft, { payload }) => {
      draft.transaction = payload;
    });

    builder.addCase(getTransactionIds.fulfilled, (draft, { payload }) => {
      draft.transactionIds = payload;
    });

    builder.addCase(getTransactionFiles.fulfilled, (draft, { payload }) => {
      draft.files = payload;
    });

    builder.addCase(deleteTransactionFile.fulfilled, (draft, { payload }) => {
      draft.files = draft.files.filter(file => file.id !== payload.fileId);

      const transaction = draft.transactions.list.find(
        el => el.id === payload.transactionId,
      );

      if (transaction) {
        transaction.noOfFiles = +transaction.noOfFiles - 1;
      }
    });

    builder.addCase(addTransactionFiles.fulfilled, (draft, { payload }) => {
      draft.files.push(...payload.data.uploaded);

      const transaction = draft.transactions.list.find(
        el => el.id === payload.transactionId,
      );

      if (transaction) {
        transaction.noOfFiles =
          +transaction.noOfFiles + payload.data.uploaded.length;
      }

      if (payload.data.failed.length) {
        errorNotification(
          new Error(`${payload.data.failed.join(', ')} failed to upload`),
        );
      }
    });

    builder.addMatcher(
      isAnyOf(
        getBankTransactions.rejected,
        getTransactions.rejected,
        getTransaction.rejected,
        getTransactionIds.rejected,
        getTransactionFiles.rejected,
        deleteTransactionFile.rejected,
        addTransactionFiles.rejected,
      ),
      (_Draft, { error }) => {
        errorNotification(error);
      },
    );
  },
});

export const {
  clearTransactions,
  clearTransaction,
  clearTransactionIds,
  clearTransactionFiles,
  clearBankTransactions,
} = transactions.actions;

export default transactions.reducer;
